import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate, useOutletContext } from 'react-router-dom';

import { transactionFlowsSelector, useCreateTransactionMutation } from '@/api/v1';
import { ExpectedSettlementTimeInfo } from '@/components/molecules/ExpectedSettlementTimeInfo';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { AmountAndCurrencyWidget } from '@/components/organisms/AmountAndCurrencyWidget';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { TransactionDetailsAccordion } from '@/components/organisms/TransactionDetailsAccordion';
import { useSimulation } from '@/hooks/useSimulation';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';
import {
  DepositFlowAction,
  DepositFlowState,
  fromAmountChanged,
  simulationCreated,
  toAmountChanged,
} from '@/Screens/Dashboard/Flows/DepositFlow/reducer';
import {
  CurrencyCodeType,
  DashboardOperation,
  DashboardOperationDestination,
} from '@/types/currency';
import { MarketExecutionType, TransactionRequest, TransactionResponse } from '@/types/transaction';
import { OutletReducer } from '@/utility/reducers';
import { transactionResponseToNavigationState } from '@/utility/transactionResponseToNavigationState';

interface Props {
  onClose(): void;
}

enum SubmitError {
  REQUIRED_AMOUNT = 'REQUIRED_AMOUNT',
}

export const DepositReview: React.FC<Props> = (props: Props): React.ReactElement => {
  const toast = useToast();

  const [error, setError] = React.useState<SubmitError | null>(null);
  const [state, dispatch] = useOutletContext<OutletReducer<DepositFlowState, DepositFlowAction>>();
  const [createTransaction, createTransactionResult] = useCreateTransactionMutation();

  const { onClose } = props;

  const selector = useSelector(transactionFlowsSelector('DEPOSIT'));
  const tr = useTranslator();
  const navigate = useNavigate();

  const { data: operations = [] } = selector;
  const { fromAmount, fromCurrency, toAmount, toCurrency, selectedFundingMethod, simulationId } =
    state;

  const [simulation, simulating] = useSimulation({
    simulationId: simulationId,
    fromAmount: fromAmount,
    toAmount: toAmount,
    fromCurrency: fromCurrency,
    toCurrency: toCurrency,
    fundingMethod: selectedFundingMethod,
    paymentRail: selectedFundingMethod,
  });

  const resetAmountRequiredError = React.useCallback((): void => {
    setError((error: SubmitError | null): SubmitError | null => {
      if (error && error === SubmitError.REQUIRED_AMOUNT) {
        return null;
      }
      return error;
    });
  }, []);

  const onToAmountChange = React.useCallback(
    (value: number | null): void => {
      dispatch(toAmountChanged(value));
      resetAmountRequiredError();
    },
    [resetAmountRequiredError, dispatch],
  );

  const onFromAmountChange = React.useCallback(
    (value: number | null): void => {
      dispatch(fromAmountChanged(value));
      resetAmountRequiredError();
    },
    [resetAmountRequiredError, dispatch],
  );

  const fromCurrencies = React.useMemo(
    (): readonly CurrencyCodeType[] =>
      operations.map((operation: DashboardOperation): CurrencyCodeType => operation.currency),
    [operations],
  );

  const toCurrencies = React.useMemo(
    (): readonly CurrencyCodeType[] =>
      operations
        .find((operation: DashboardOperation): boolean => fromCurrency === operation.currency)
        ?.destinations?.filter((destination: DashboardOperationDestination): boolean =>
          destination.fundingMethods.includes(selectedFundingMethod),
        )
        .map(
          (destination: DashboardOperationDestination): CurrencyCodeType => destination.currency,
        ) ?? [],
    [operations, fromCurrency, selectedFundingMethod],
  );

  const onNext = React.useCallback((): void => {
    // FIXME: does this make sense?
    if (!fromAmount || !toAmount) {
      setError(SubmitError.REQUIRED_AMOUNT);
      return;
    }

    if (!simulation.id || simulation.id === '') {
      console.debug('no simulation id');
      return;
    }

    const transactionRequest: TransactionRequest = {
      simulationId: simulation.id,
      beneficiaryIp: '0.0.0.0',
      marketExecutionType: MarketExecutionType.executeImmediately,
    };
    createTransaction(transactionRequest);
  }, [fromAmount, toAmount, simulation.id, createTransaction]);

  const tryToNavigateToPayment = React.useCallback(
    (response: TransactionResponse): void => {
      const search = new URLSearchParams();

      search.set('transactionId', response.id);
      navigate(
        {
          pathname: '../payment',
          search: search.toString(),
        },
        {
          state: transactionResponseToNavigationState(response, selectedFundingMethod),
        },
      );
    },
    [selectedFundingMethod, navigate],
  );

  React.useEffect((): void => {
    switch (createTransactionResult.status) {
      case QueryStatus.fulfilled:
        tryToNavigateToPayment(createTransactionResult.data);
        break;
      case QueryStatus.rejected:
        toast.show(tr('AN_ERROR_OCCURRED'), 'error');
        break;
      default:
        break;
    }
  }, [
    createTransactionResult.data,
    createTransactionResult.status,
    navigate,
    tryToNavigateToPayment,
    toast,
    tr,
  ]);

  React.useEffect((): void => {
    if (simulation.id === '') {
      return;
    }

    dispatch(simulationCreated(simulation));
  }, [dispatch, simulation]);

  // If we don't have the local state with the funding method,
  // we redirect to the initial step to complete.
  if (!selectedFundingMethod) {
    return <Navigate to=".." replace={true} />;
  }

  return (
    <ApplicationModal title={tr('REVIEW_CONVERSION')} back=".." onClose={onClose}>
      <Box position="relative" mb={2}>
        <AmountAndCurrencyWidget
          label={tr('YOU_SEND')}
          size="normal"
          amount={fromAmount}
          currency={fromCurrency}
          currencies={fromCurrencies}
          error={error === SubmitError.REQUIRED_AMOUNT}
          currencyDisabled={true}
          disabled={simulating}
          placeholder={simulating && fromAmount === null}
          onAmountChange={onFromAmountChange}
        />
      </Box>
      <Box mb={3}>
        <AmountAndCurrencyWidget
          label={tr('AMOUNT_ADDED_TO_WALLET')}
          size="normal"
          amount={toAmount}
          currency={toCurrency}
          currencies={toCurrencies}
          disabled={simulating}
          placeholder={simulating && toAmount === null}
          onAmountChange={onToAmountChange}
        />
      </Box>

      <TransactionDetailsAccordion
        simulating={simulating}
        simulation={simulation}
        fromAmount={fromAmount}
        fromCurrency={fromCurrency}
      />

      <Box sx={styles.expectedSettlementTimeInfo}>
        <ExpectedSettlementTimeInfo
          expectedSettlementTimeDetails={
            simulation.transactionDetails.expectedSettlementTimeDetails
          }
        />
      </Box>

      <Box mt={5}>
        <LoadingButton
          variant="dark"
          fullWidth={true}
          loading={createTransactionResult.isLoading}
          disabled={!simulation.id || createTransactionResult.isLoading}
          onClick={onNext}>
          {tr('CONTINUE')}
        </LoadingButton>
      </Box>
      <Box mt={2} display="flex" justifyContent="center">
        {error ? (
          <Typography variant="body2" color="error">
            {tr(error)}
          </Typography>
        ) : null}
      </Box>
    </ApplicationModal>
  );
};

const styles: Record<string, SystemStyleObject> = {
  expectedSettlementTimeInfo: {
    display: 'flex',
    justifyContent: 'center',
    mt: 3,
    color: 'primary.main',
  },
};
